import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Users2, Phone, Mail, Search, Pencil, Trash2, X, Building2, StickyNote, Star, KeyRound,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../components/ConfirmProvider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

const EMPTY_FORM = {
  name: "", firm_name: "", phone: "", email: "", notes: "", specialties: [],
};

export default function ContractorsPage() {
  const { user } = useAuth();
  const canEdit = ["admin", "pm", "designer"].includes(user?.role);
  const confirm = useConfirm();
  const [contractors, setContractors] = useState([]);
  const [milestoneOptions, setMilestoneOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | { new: true } | contractorObj
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const results = await Promise.all([
        api.get("/workspace/contractors"),
        user?.role === "admin" ? api.get("/firm/settings") : Promise.resolve({ data: {} }),
      ]);
      setContractors(results[0].data.contractors || []);
      const templates = results[1].data?.firm?.milestone_templates || [];
      setMilestoneOptions(templates);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing({ new: true });
  };
  const openEdit = (c) => {
    setForm({
      name: c.name || "",
      firm_name: c.firm_name || "",
      phone: c.phone || "",
      email: c.email || "",
      notes: c.notes || "",
      specialties: c.specialties || [],
    });
    setEditing(c);
  };
  const closeDialog = () => { setEditing(null); setForm(EMPTY_FORM); };

  const toggleSpec = (name) => {
    setForm((f) => ({
      ...f,
      specialties: f.specialties.includes(name)
        ? f.specialties.filter((s) => s !== name)
        : [...f.specialties, name],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const firm = form.firm_name.trim();
    const phone = form.phone.trim();
    const email = form.email.trim();
    if (!name || !firm || !phone) {
      toast.error("Please fill in name, firm and mobile number");
      return;
    }
    if (!form.specialties.length) {
      toast.error("Select at least one specialty");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name, firm_name: firm, phone,
        email: email || null,
        specialties: form.specialties,
        notes: form.notes.trim() || null,
      };
      let contractorId;
      let inviteInfo = null;
      if (editing?.contractor_id) {
        const { data } = await api.patch(`/workspace/contractors/${editing.contractor_id}`, payload);
        contractorId = editing.contractor_id;
        inviteInfo = data?.invite;
        toast.success("Contractor updated");
      } else {
        const { data } = await api.post("/workspace/contractors", payload);
        contractorId = data.contractor.contractor_id;
        inviteInfo = data?.invite;
        toast.success("Contractor added");
      }
      // Upload logo if a file was selected
      if (form._logoFile && contractorId) {
        const fd = new FormData();
        fd.append("file", form._logoFile);
        try {
          await api.post(`/workspace/contractors/${contractorId}/logo`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) { toast.error(`Logo upload failed: ${formatApiError(err)}`); }
      } else if (form._logoRemove && contractorId) {
        try { await api.delete(`/workspace/contractors/${contractorId}/logo`); }
        catch { /* non-fatal */ }
      }
      closeDialog();
      await load();
      // Show login credentials to admin so they can share with the contractor
      if (inviteInfo?.invite_type === "google") {
        setInviteResult({ email, invite_type: "google" });
      } else if (inviteInfo?.invite_type === "password" && inviteInfo?.temp_password) {
        setInviteResult({ email, invite_type: "password", temp_password: inviteInfo.temp_password });
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  // Enable / rotate mobile-login credentials for a contractor who has no email
  // (or prefers phone login). Reveals phone + generated password to the admin
  // so they can WhatsApp/SMS it to the contractor manually.
  const enableMobileLogin = async (c) => {
    if (!c.phone) {
      toast.error("This contractor has no mobile number on file");
      return;
    }
    const isReset = !!c.user_id;
    if (isReset) {
      const ok = await confirm({
        title: `Reset ${c.name}'s mobile password?`,
        message: "Their current password will stop working immediately. You'll get a new one to share.",
        confirmText: "Reset password",
        danger: true,
      });
      if (!ok) return;
    }
    try {
      const { data } = await api.post(`/workspace/contractors/${c.contractor_id}/mobile-login`);
      setInviteResult({
        invite_type: "mobile",
        phone: data.phone,
        temp_password: data.temp_password,
        contractor_name: c.name,
        was_reset: isReset,
      });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (c) => {
    const ok = await confirm({
      title: `Remove ${c.name}?`,
      message: "They'll no longer appear when assigning tasks. Any past assignments stay untouched.",
      confirmText: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/workspace/contractors/${c.contractor_id}`);
      toast.success("Contractor removed");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleFavorite = async (c) => {
    const next = !c.is_favorite;
    // Optimistic update
    setContractors((prev) => prev.map((x) => x.contractor_id === c.contractor_id ? { ...x, is_favorite: next } : x));
    try {
      await api.patch(`/workspace/contractors/${c.contractor_id}`, { is_favorite: next });
      // Reload to re-sort favorites to the top
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
      // Revert
      setContractors((prev) => prev.map((x) => x.contractor_id === c.contractor_id ? { ...x, is_favorite: !next } : x));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contractors.filter((c) => {
      if (specialtyFilter !== "all") {
        if (!(c.specialties || []).includes(specialtyFilter)) return false;
      }
      if (!q) return true;
      return [c.name, c.firm_name, c.email, c.phone, ...(c.specialties || [])]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [contractors, search, specialtyFilter]);

  const allSpecialties = useMemo(() => {
    const s = new Set(milestoneOptions);
    contractors.forEach((c) => (c.specialties || []).forEach((x) => s.add(x)));
    return [...s];
  }, [milestoneOptions, contractors]);

  return (
    <div className="max-w-6xl mx-auto py-10">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Directory</div>
          <h1 className="font-display text-4xl font-bold text-[#2C2A29] mt-2">Contractors</h1>
          <p className="text-[#7A7571] mt-2 max-w-2xl">
            Your pool of trusted trades. Project managers pick from this list when assigning tasks.
          </p>
        </div>
        {canEdit && (
          <button onClick={openNew} data-testid="add-contractor-btn"
            className="btn-primary inline-flex items-center gap-2 flex-shrink-0">
            <Plus size={16} /> Add contractor
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A7571]" />
          <input
            className="input-soft !pl-11"
            placeholder="Search by name, firm, phone, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="contractor-search"
          />
        </div>
        <select className="input-soft sm:w-64" value={specialtyFilter}
          onChange={(e) => setSpecialtyFilter(e.target.value)}
          data-testid="contractor-filter">
          <option value="all">All specialties</option>
          {allSpecialties.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-[#7A7571]">
          <Loader2 size={20} className="animate-spin" />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="card-soft text-center py-16">
          <Users2 size={40} className="mx-auto text-[#7A7571] mb-4" />
          <h3 className="font-display text-xl font-bold text-[#2C2A29] mb-1">
            {contractors.length === 0 ? "No contractors yet" : "No matching contractors"}
          </h3>
          <p className="text-sm text-[#7A7571] mb-4">
            {contractors.length === 0
              ? "Start by adding the trades you regularly work with."
              : "Try clearing the filters."}
          </p>
          {canEdit && contractors.length === 0 && (
            <button onClick={openNew} className="btn-primary inline-flex items-center gap-2">
              <Plus size={16} /> Add your first contractor
            </button>
          )}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 gap-4">
          {filtered.map((c) => (
            <ContractorCard key={c.contractor_id} c={c}
              onEdit={canEdit ? openEdit : null}
              onDelete={canEdit ? del : null}
              onToggleFavorite={canEdit ? toggleFavorite : null}
              onEnableMobileLogin={canEdit ? enableMobileLogin : null} />
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && closeDialog()}>
        <DialogContent className="max-w-[800px] w-[95vw] rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="contractor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing?.new ? "Add contractor" : "Edit contractor"}
            </DialogTitle>
            <DialogDescription>
              Contractors added here become selectable when assigning tasks.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <LogoPicker
              initialUrl={editing?.logo_url}
              contractorName={form.name}
              onChange={(file, remove) => setForm((f) => ({ ...f, _logoFile: file, _logoRemove: remove }))}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Contractor name" required>
                <input required className="input-soft" data-testid="contractor-name"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Contractor firm" required>
                <input required className="input-soft" placeholder="e.g. ABC Renovation" data-testid="contractor-firm"
                  value={form.firm_name} onChange={(e) => setForm({ ...form, firm_name: e.target.value })} />
              </Field>
              <Field label="Phone" required>
                <input required className="input-soft" placeholder="+65 9123 4567" data-testid="contractor-phone"
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </Field>
              <Field label="Email">
                <input type="email" className="input-soft" placeholder="Optional" data-testid="contractor-email"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Field>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">
                Specialties <span className="text-[#E07A5F]">*</span>
                {milestoneOptions.length === 0 && (
                  <span className="text-[10px] text-[#7A7571] normal-case tracking-normal ml-1">
                    (define milestones in Settings → Milestone library to pre-populate this)
                  </span>
                )}
              </label>
              {allSpecialties.length === 0 ? (
                <input className="input-soft" placeholder="e.g. Carpentry, Electrical (comma-separated)"
                  value={form.specialties.join(", ")}
                  onChange={(e) => setForm({ ...form, specialties: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  data-testid="contractor-specialties-text"
                />
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="contractor-specialties">
                  {allSpecialties.map((name) => {
                    const active = form.specialties.includes(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => toggleSpec(name)}
                        data-testid={`specialty-chip-${name}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition ${
                          active
                            ? "bg-[#E07A5F] text-white border-[#E07A5F]"
                            : "bg-white text-[#2C2A29] border-[#EBE5D9] hover:bg-[#F7F4EC]"
                        }`}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <Field label="Notes">
              <textarea className="input-soft min-h-[80px]" data-testid="contractor-notes"
                placeholder="Rates, availability, preferences…"
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
            <div className="rounded-2xl bg-[#FBF7EF] border border-[#EBE5D9] px-4 py-3 text-xs text-[#7A7571] flex items-start gap-2">
              <Mail size={14} className="mt-0.5 flex-shrink-0" />
              <span>
                Email is optional. If you enter one, we&apos;ll try to set up Google or password sign-in for the contractor.
                For contractors without email, add them anyway and use <span className="font-semibold">Enable mobile login</span> on their card to generate a phone + password login you can share via WhatsApp / SMS.
              </span>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={closeDialog} className="btn-secondary flex-1">Cancel</button>
              <button type="submit" disabled={saving} data-testid="contractor-submit"
                className="btn-primary flex-1 inline-flex items-center justify-center gap-2">
                {saving && <Loader2 size={16} className="animate-spin" />}
                {saving ? "Saving…" : editing?.new ? "Add contractor" : "Save changes"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!inviteResult} onOpenChange={(v) => { if (!v) setInviteResult(null); }}>
        <DialogContent className="max-w-lg rounded-3xl" data-testid="contractor-invite-result">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {inviteResult?.invite_type === "mobile"
                ? (inviteResult?.was_reset ? "Mobile password reset" : "Mobile login ready")
                : "Contractor invited"}
            </DialogTitle>
            <DialogDescription>
              {inviteResult?.invite_type === "mobile" ? (
                <>Share these details with <span className="font-semibold text-[#2C2A29]">{inviteResult?.contractor_name}</span> via WhatsApp or SMS so they can sign in on the <span className="font-semibold">Mobile</span> tab of the sign-in page.</>
              ) : (
                <>An OrkaiFlow login was set up for <span className="font-semibold text-[#2C2A29]">{inviteResult?.email}</span>. Share these details so they can access their assigned tasks.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {inviteResult?.invite_type === "google" ? (
            <div className="card-soft !bg-[#EEF2FF] !py-4">
              <div className="text-xs uppercase tracking-widest font-bold text-[#3730A3] mb-2">
                Google sign-in
              </div>
              <div className="text-sm text-[#2C2A29]">
                Because this is a Gmail address, they can sign in immediately using
                <span className="font-semibold"> Continue with Google</span> on the login page — no password needed.
              </div>
            </div>
          ) : inviteResult?.invite_type === "password" ? (
            <div className="card-soft !bg-[#FFFBEB] !py-4">
              <div className="text-xs uppercase tracking-widest font-bold text-[#92400E] mb-2">
                Temporary password
              </div>
              <div className="text-sm text-[#2C2A29] mb-3">
                Send them this login on the Email tab of the sign-in page:
              </div>
              <div className="flex items-center gap-2 bg-white rounded-2xl border border-[#EBE5D9] p-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">Email</div>
                  <div className="font-mono text-sm truncate">{inviteResult.email}</div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mt-2">Password</div>
                  <div className="font-mono text-sm select-all">{inviteResult.temp_password}</div>
                </div>
                <button
                  className="btn-secondary text-xs"
                  onClick={() => {
                    navigator.clipboard.writeText(`Email: ${inviteResult.email}\nPassword: ${inviteResult.temp_password}`);
                    toast.success("Copied to clipboard");
                  }}
                >
                  Copy
                </button>
              </div>
              <p className="text-[11px] text-[#7A7571] mt-2">
                They should change this password after their first sign-in.
              </p>
            </div>
          ) : inviteResult?.invite_type === "mobile" ? (
            <div className="card-soft !bg-[#FFFBEB] !py-4">
              <div className="text-xs uppercase tracking-widest font-bold text-[#92400E] mb-2">
                Mobile + password
              </div>
              <div className="text-sm text-[#2C2A29] mb-3">
                They&apos;ll sign in on the <span className="font-semibold">Mobile</span> tab using:
              </div>
              <div className="flex items-center gap-2 bg-white rounded-2xl border border-[#EBE5D9] p-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">Mobile</div>
                  <div className="font-mono text-sm truncate" data-testid="mobile-invite-phone">{inviteResult.phone}</div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mt-2">Password</div>
                  <div className="font-mono text-sm select-all" data-testid="mobile-invite-password">{inviteResult.temp_password}</div>
                </div>
                <button
                  data-testid="mobile-invite-copy"
                  className="btn-secondary text-xs"
                  onClick={() => {
                    navigator.clipboard.writeText(`Mobile: ${inviteResult.phone}\nPassword: ${inviteResult.temp_password}`);
                    toast.success("Copied to clipboard");
                  }}
                >
                  Copy
                </button>
              </div>
              <p className="text-[11px] text-[#7A7571] mt-2">
                Ask them to change this password after their first sign-in. Regenerate any time from the contractor card.
              </p>
            </div>
          ) : null}
          <div className="flex items-center justify-end pt-2">
            <button className="btn-primary" onClick={() => setInviteResult(null)}>
              Got it
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LogoPicker({ initialUrl, contractorName, onChange }) {
  const [preview, setPreview] = useState(initialUrl || null);
  const [file, setFile] = useState(null);
  const inputRef = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/svg+xml"].includes(f.type)) {
      toast.error("Logo must be PNG, JPG or SVG"); return;
    }
    if (f.size > 2_000_000) { toast.error("Logo must be under 2MB"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    onChange(f, false);
  };
  const remove = () => {
    setFile(null);
    setPreview(null);
    onChange(null, !!initialUrl);
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-20 h-20 rounded-2xl border border-[#EBE5D9] bg-[#FBF7EF] flex items-center justify-center overflow-hidden flex-shrink-0"
        data-testid="contractor-logo-preview">
        {preview ? (
          <img src={preview} alt={contractorName} className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs text-[#7A7571]">Logo</span>
        )}
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1">Logo <span className="normal-case tracking-normal text-[10px]">(optional)</span></div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => inputRef.current?.click()}
            data-testid="contractor-logo-upload" className="btn-secondary text-sm !py-1.5 !px-3">
            {file || initialUrl ? "Replace" : "Upload"}
          </button>
          {(file || initialUrl) && (
            <button type="button" onClick={remove}
              data-testid="contractor-logo-remove" className="btn-ghost text-sm !py-1.5 !px-3 text-[#9F1239]">
              Remove
            </button>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/svg+xml"
          className="hidden" onChange={pick} data-testid="contractor-logo-input" />
      </div>
    </div>
  );
}

function ContractorCard({ c, onEdit, onDelete, onToggleFavorite, onEnableMobileLogin }) {
  const initials = (c.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const logoSrc = c.logo_url ? `${c.logo_url}?v=${c.contractor_id}` : null;
  const hasMobileLogin = !!c.user_id && c.login_method === "mobile";
  return (
    <div className={`card-soft transition ${c.is_favorite ? "ring-1 ring-[#F2CC8F]" : ""}`} data-testid={`contractor-${c.contractor_id}`}>
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-2xl bg-[#F7F4EC] border border-[#EBE5D9] flex items-center justify-center font-bold text-[#7A7571] flex-shrink-0 overflow-hidden">
          {logoSrc ? <img src={logoSrc} alt={c.name} className="w-full h-full object-cover" /> : initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {onToggleFavorite && (
                  <button
                    onClick={() => onToggleFavorite(c)}
                    data-testid={`contractor-fav-${c.contractor_id}`}
                    className="btn-ghost !p-1 flex-shrink-0"
                    title={c.is_favorite ? "Unfavorite" : "Mark as favorite"}
                  >
                    <Star
                      size={18}
                      className={c.is_favorite ? "text-[#F2CC8F]" : "text-[#B8B0A6] hover:text-[#F2CC8F]"}
                      fill={c.is_favorite ? "#F2CC8F" : "none"}
                    />
                  </button>
                )}
                <div className="font-display text-lg font-bold text-[#2C2A29] truncate">{c.name}</div>
              </div>
              {c.firm_name && (
                <div className="text-sm text-[#7A7571] inline-flex items-center gap-1 mt-0.5">
                  <Building2 size={13} /> {c.firm_name}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {onEnableMobileLogin && c.phone && (
                <button
                  onClick={() => onEnableMobileLogin(c)}
                  data-testid={`contractor-mobile-login-${c.contractor_id}`}
                  className={`btn-ghost !p-1.5 ${hasMobileLogin ? "text-[#0F766E]" : "text-[#7A7571] hover:text-[#2C2A29]"}`}
                  title={hasMobileLogin ? "Reset mobile password" : "Enable mobile login"}
                >
                  <KeyRound size={14} />
                </button>
              )}
              {onEdit && (
                <button onClick={() => onEdit(c)} data-testid={`contractor-edit-${c.contractor_id}`}
                  className="btn-ghost !p-1.5 text-[#7A7571] hover:text-[#2C2A29]" title="Edit">
                  <Pencil size={14} />
                </button>
              )}
              {onDelete && (
                <button onClick={() => onDelete(c)} data-testid={`contractor-delete-${c.contractor_id}`}
                  className="btn-ghost !p-1.5 text-[#9F1239]" title="Remove">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mt-2">
            {c.phone && (
              <a href={`tel:${c.phone}`} className="inline-flex items-center gap-2 text-[#2C2A29] hover:text-[#E07A5F]">
                <Phone size={13} className="text-[#7A7571]" /> {c.phone}
              </a>
            )}
            {c.email && (
              <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 text-[#2C2A29] hover:text-[#E07A5F] truncate">
                <Mail size={13} className="text-[#7A7571]" /> {c.email}
              </a>
            )}
          </div>
          {(c.specialties || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {c.specialties.map((s) => (
                <span key={s} className="px-2.5 py-1 rounded-full bg-[#FBF7EF] border border-[#EBE5D9] text-[11px] font-semibold text-[#7A7571]">
                  {s}
                </span>
              ))}
            </div>
          )}
          {c.notes && (
            <div className="mt-3 pt-3 border-t border-[#EBE5D9] text-xs text-[#7A7571] flex items-start gap-2">
              <StickyNote size={12} className="mt-0.5 flex-shrink-0" />
              <span className="line-clamp-3">{c.notes}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">
        {label}{required && <span className="text-[#E07A5F]"> *</span>}
      </label>
      {children}
    </div>
  );
}

